import SdIconDark from 'assets/branding/SdIcon-dark.svg';
import SdIconLight from 'assets/branding/SdIcon-light.svg';
import SdLogoDark from 'assets/branding/SMARTdiagnostics-dark.svg';
import SdLogoLight from 'assets/branding/SMARTdiagnostics-light.svg';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';

export const NavigationDrawerTransitionMs = 300;

/**
 * Shared active style for primary navigation items (used by NavLink activeStyle prop)
 */
export const NAV_ACTIVE_STYLE = {
    backgroundColor: tokens.nav.secondarySelected,
    color: tokens.nav.textSelected,
} as const;

/**
 * Active style for secondary navigation items (submenu items)
 * Uses subtle background instead of solid color
 */
export const NAV_SECONDARY_ACTIVE_STYLE = {
    backgroundColor: tokens.nav.secondaryItemSelected,
} as const;

export const NavigationDrawerWrapper = styled.nav<{ $expanded: boolean }>`
    display: grid;
    grid-template-rows: min-content auto min-content;
    min-height: ${tokens.nav.itemSize};
    width: ${({ $expanded }) =>
        $expanded ? tokens.nav.expandedWidth : tokens.nav.itemSize};
    z-index: 30;
    overflow: hidden;
    color: ${tokens.nav.text};
    background-color: ${tokens.nav.primaryBackground};
    border-right: 0.1rem solid ${tokens.nav.border};
    transition: width ${NavigationDrawerTransitionMs}ms ease-in-out;

    @media (prefers-reduced-motion) {
        transition: none;
    }

    @media ${MediaQueries.smallAndDown} {
        display: ${({ $expanded }) => ($expanded ? 'grid' : 'none')};
        width: ${tokens.nav.expandedWidth};
    }
`;

export const NavigationDrawerHeaderWrapper = styled.div<{ $expanded?: boolean }>`
    display: flex;
    min-height: 7rem;
    width: ${({ $expanded }) =>
        $expanded ? tokens.nav.expandedWidth : tokens.nav.itemSize};
    position: relative;
    justify-content: ${({ $expanded }) => ($expanded ? 'right' : 'center')};
    align-items: center;
    color: ${tokens.nav.icon};
    cursor: pointer;
    transition: width ${NavigationDrawerTransitionMs}ms ease-in-out;

    &:hover {
        background-color: ${tokens.nav.hoverBackground};
    }

    @media (prefers-reduced-motion) {
        transition: none;
    }

    @media ${MediaQueries.smallAndDown} {
        width: ${tokens.nav.itemSize};
        right: 0rem;
        background-color: transparent;
        color: ${tokens.text.primary};

        &:hover {
            color: ${tokens.text.primary};
        }
    }
`;

export const NavigationDrawerItems = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
`;

export const NavigationItemAccordion = styled.div`
    display: flex;
    flex-direction: column;
`;

export const NavigationItemIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${tokens.nav.itemSize};
    height: ${tokens.nav.itemSize};
`;

export const NavigationItemLeftGroup = styled.div`
    display: flex;
    align-items: center;
`;

export const NavigationItemTitle = styled.span<{ $secondary?: boolean }>`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    font-weight: ${tokens.font.weightLight};
    padding: ${({ $secondary }) => ($secondary ? '0 2rem' : '0rem')};
    height: ${({ $secondary }) => ($secondary ? tokens.nav.itemSize : '')};
    transition: all ${NavigationDrawerTransitionMs}ms ease-in-out;
    cursor: pointer;

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationItemHeader = styled.div`
    display: flex;
    width: 100%;
    min-height: ${tokens.nav.itemSize};
    align-items: center;
    justify-content: space-between;
    padding-right: 2.4rem;
`;

export const NavigationItemChevron = styled.span`
    color: ${tokens.nav.chevron};

    .active & {
        color: ${tokens.nav.textSelected};
    }
`;

export const SecondaryNavigationItemsWrapper = styled.div<{
    $show: boolean;
    $inPopup?: boolean;
}>`
    overflow: hidden;
    max-height: ${({ $show }) => ($show ? '100rem' : '0rem')};
    background-color: transparent;
    border-bottom: ${({ $show, $inPopup }) =>
        $show && !$inPopup ? '0.1rem solid var(--nav-border)' : 'none'};
    border-radius: ${({ $inPopup }) => ($inPopup ? '0 0 0.8rem 0.8rem' : '0')};
    transition: ${({ $show }) =>
        $show
            ? 'max-height 0.3s ease-in-out'
            : 'max-height 0.3s cubic-bezier(0, 1, 0, 1)'};

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const SecondaryNavigationItemHeader = styled(NavLink)`
    display: flex;
    height: 5rem;
    padding: 0rem 2.5rem;
    align-items: center;
    text-decoration: none;
    color: ${tokens.nav.text};
    transition: all 0.3s ease-in-out;

    &:hover {
        background: ${tokens.nav.hoverBackground};
        color: ${tokens.nav.text};

        ${NavigationItemTitle} {
            transform: translateX(0.4rem) scale(1.01);
            transition: transform 0.3s ease-in-out;

            @media (prefers-reduced-motion) {
                transform: none;
                transition: none;
            }
        }

        @media (prefers-reduced-motion) {
            transition: none;
        }
    }

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationDrawerFooterWrapper = styled.div<{ $expanded: boolean }>`
    display: flex;
    flex-direction: row;
    width: ${({ $expanded }) =>
        $expanded ? tokens.nav.expandedWidth : tokens.nav.itemSize};
    height: ${({ $expanded }) =>
        $expanded ? 'unset' : 'calc(2 * var(--nav-item-size))'};
    background: transparent;
    transition: all 0.3s ease-in-out;

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationFooterHeader = styled.div`
    display: grid;
    min-height: ${tokens.nav.itemSize};
    align-items: center;
    grid-template-columns: 1fr ${tokens.nav.itemSize};
    color: ${tokens.icon.default};
    cursor: pointer;
`;

export const NavigationFooterIcon = styled.div<{ $expanded: boolean }>`
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    cursor: pointer;

    ${({ $expanded }) =>
        $expanded
            ? css`
                  width: 22rem;
                  height: 5rem;
                  margin-left: 0.5rem;
                  background-image: url('${SdLogoLight}');
                  transition: width 0.3s ease-in-out;

                  [data-theme='dark'] &,
                  [data-theme='night'] & {
                      background-image: url('${SdLogoDark}');
                  }

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `
            : css`
                  height: ${tokens.nav.itemSize};
                  width: ${tokens.nav.itemSize};
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  &:hover {
                      background-color: ${tokens.nav.hoverBackground};
                  }

                  &::after {
                      content: '';
                      display: block;
                      width: 2rem;
                      height: 2rem;
                      background-image: url('${SdIconLight.replaceAll("'", '%27')}');
                      background-size: contain;
                      background-repeat: no-repeat;
                      transform: scale(1.5);
                  }

                  [data-theme='dark'] &::after,
                  [data-theme='night'] &::after {
                      background-image: url('${SdIconDark.replaceAll("'", '%27')}');
                  }

                  transition: all 0.3s ease-in-out;

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `};
`;

export const NavigationChatbotIcon = styled.div<{ $expanded: boolean }>`
    cursor: pointer;
    background: transparent;
    color: ${tokens.nav.icon};

    font-family: ${tokens.font.familyBase};
    font-weight: ${tokens.font.weightLight};
    font-style: normal;

    ${({ $expanded }) =>
        $expanded
            ? css`
                  width: ${tokens.nav.expandedWidth};
                  height: ${tokens.nav.itemSize};
                  transition: width 0.3s ease-in-out;
                  border-right: 1px solid ${tokens.nav.border};

                  align-items: center;
                  display: flex;
                  justify-content: align-start;
                  gap: 2rem;
                  padding-left: 2rem;

                  &:hover {
                      background-color: ${tokens.nav.hoverBackground};
                      ${NavigationItemTitle} {
                          transform: translateX(0.4rem) scale(1.01);
                          transition: transform ${NavigationDrawerTransitionMs}ms
                              ease-in-out;

                          @media (prefers-reduced-motion) {
                              transform: none;
                              transition: none;
                          }
                      }
                  }

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `
            : css`
                  height: ${tokens.nav.itemSize};
                  width: ${tokens.nav.itemSize};
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  &:hover {
                      background-color: ${tokens.nav.hoverBackground};
                  }

                  transition: all 0.3s ease-in-out;

                  @media (prefers-reduced-motion) {
                      transition: none;
                  }
              `};
`;

export const NavigationFooterItems = styled.div<{ $show: boolean }>`
    overflow: hidden;
    max-height: ${({ $show }) => ($show ? '50rem' : '0rem')};
    transition: ${({ $show }) =>
        $show ? 'all 0.3s ease-in-out' : 'all 0.3s cubic-bezier(0, 1, 0, 1)'};

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;

export const NavigationFooterContent = styled.div`
    display: flex;
    flex-direction: column;
    background: transparent;
`;

export const NavigationItem = styled(NavLink)<{
    $isDrawerExpanded?: boolean;
    $isExpanded?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-decoration: none;
    min-height: ${tokens.nav.itemSize};
    transition-property: background, width;
    transition-timing-function: ease-in-out;
    color: ${tokens.nav.text};
    background: ${({ $isExpanded }) =>
        $isExpanded ? tokens.nav.hoverBackground : 'transparent'};
    transition-duration: ${({ $isDrawerExpanded }) =>
        $isDrawerExpanded ? '0.5s' : 'none'};

    &:hover {
        background: ${tokens.nav.hoverBackground};
        transition-duration: ${({ $isDrawerExpanded }) =>
            $isDrawerExpanded ? '0.3s' : 'none'};

        @media (prefers-reduced-motion) {
            transition: none;
        }
    }

    @media (prefers-reduced-motion) {
        transition: none;
    }
`;
